import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Config from '../config/siteConfig.json'; // Import the config file
import './Layout.css';
import config from '../configLoader';
import { useSiteContext } from '../SiteContext';
import { LayoutProps } from "../interfaces/LayoutInterface";
import { Page } from '../interfaces/PagesInterface';
import { TimesheetType } from '../interfaces/TimesheetInterface';
import {darkenColor} from '../util/ColorUtil';
import ActionButton from './ActionButton';
import { useAdminSession } from '../hooks/useAdminSession';
import { can, canViewWorkOrders } from '../util/adminPermissions';
import CompanyLogo from './CompanyLogo';

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { userName, setPage, translator, userRole, page, miscTaskApprovalConfigs, pendingTimeOffCount } = useSiteContext();
  const { loggedIn, employeeOnly, loginError, handleLogin, handleLogout } = useAdminSession();
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
  const [selectedPage, setSelectedPage] = useState<Page | null>(Page.Dashboard); // Track the selected page

  const companyLogo = config.logo;

  // Toggle accordion category
  const toggleCategory = (category: string) => {
    setOpenCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Group pages into categories
  const pageCategories = {
    Home: [Page.Dashboard],
    Reports: [
      Page.AuditReport,
      Page.AttendanceReport,
      ...(config.showRiceServicesReports ? [Page.TimesheetDeadlineReport, Page.WeeklyOpsReport] : []),
    ],
    // showWcdpBatchExport (TAS-682): Paycor V2 and V2 Batch Export are replaced by the board.
    // V1 pages follow allowV1Export; PRDA Local has its own flag (absent = allowV1Export).
    Paycor: [
      ...(config.allowV1Export ? [Page.PaycorDownload, Page.ManualPaycor] : []),
      ...(config.allowV1Export && !config.showWcdpBatchExport ? [Page.PaycorDownloadV2] : []),
      ...((config.showPrdaLocal ?? config.allowV1Export) ? [Page.PaycorPrdaLocal] : []),
    ],
    ImportExport: [
      Page.WCDPPaintSingleFamilyImport,
      Page.WCDPPaintMultiFamilyImport,
      Page.WCDPPaintMultiFamilyImportV1,
      Page.WCDPDrywallSingleFamilyImport,
      Page.WCDPDrywallMultiFamilyImport,
    ],
    V1Export: [Page.V1Supervisors, Page.V1BatchExport],
    V2Export: [Page.V2BatchExport],
    VistaExport: [Page.VistaExport],
    PrevailingWage: [Page.PWFringeTypes, Page.PWClassifications, Page.PWProjectSetup],
  };

  // Check if a category should be visible based on user permissions and config.
  // Gating migrated to the Approval Access permission columns (spec §3.3 / D15):
  //   Paycor / V1 / V2 exports → ExportPayroll; Vista → ExportAccounting|ExportPayroll;
  //   WCDP imports → ImportTasks; Reports → ViewReports. PrevailingWage stays super-admin.
  const shouldShowCategory = (category: string) => {
    if (category === 'Home' || category === 'EmployeeView') return true;
    if (category === 'V1Export' && (!config.allowV1Export || !can(userRole, 'exportPayroll'))) return false;
    if (category === 'V2Export' && (!config.allowV2Export || config.showWcdpBatchExport || !can(userRole, 'exportPayroll'))) return false;
    if (category === 'ImportExport' && (!config.allowWCDPProjectImport || !can(userRole, 'importTasks'))) return false;
    if (category === 'Paycor' && (pageCategories.Paycor.length === 0 || !can(userRole, 'exportPayroll'))) return false;
    if (category === 'VistaExport' && (!config.allowVistaExport || !(can(userRole, 'exportAccounting') || can(userRole, 'exportPayroll')))) return false;
    if (category === 'PrevailingWage' && (!config.showPrevailingWagePages || !userRole.admin)) return false;
    if (category === 'Reports' && (!config.showAuditReportPage || !can(userRole, 'viewReports'))) return false;
    return true;
  };

  // Handle page selection
  const handlePageClick = (page: Page) => {
    setSelectedPage(page); // Set the selected page
    setPage(page); // Navigate to the selected page
  };

  // Render accordion items
  const renderAccordion = () => {
    return (
      <>
        {/* Home Link — hidden for users without admin/payroll/supervisor/hr */}
        {(userRole.admin || userRole.payroll || userRole.supervisor || userRole.hr) && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.Dashboard ||
              selectedPage === Page.EmployeeList ||
              selectedPage === Page.SupervisorList ||
              selectedPage === Page.TimeSheetPage
              ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.Dashboard)}
          >
            {translator.translate("Home")}
          </div>
        )}
  
        {/* Time Off Approvals Link */}
        {can(userRole, 'viewTimeOffRequests') && config.showTimeOffApprovalPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.TimeOffRequestApproval ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.TimeOffRequestApproval)}
            style={{ position: 'relative' }}
          >
            {translator.translate("timeOffRequestApproval")}
            {pendingTimeOffCount > 0 && (
              <span className="pending-badge">{pendingTimeOffCount}</span>
            )}
          </div>
        )}

        {/* Timesheet Review Link */}
        {can(userRole, 'viewTimesheets') && config.showTimesheetReviewPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.TimesheetReview ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.TimesheetReview)}
          >
            {translator.translate("timesheetReview")}
          </div>
        )}

        {/* Project Work Link */}
        {can(userRole, 'viewProjectWork') && config.showProjectWorkPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.ProjectWork ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.ProjectWork)}
          >
            {translator.translate("projectWork")}
          </div>
        )}

        {/* Crew Work Link */}
        {can(userRole, 'viewProjectWork') && config.showCrewWorkPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.CrewWork ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.CrewWork)}
          >
            {translator.translate("crewWork")}
          </div>
        )}

        {/* Employee View Link */}
        {can(userRole, 'viewEmployees') && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.EmployeeView ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.EmployeeView)}
          >
            {translator.translate("EmployeeView")}
          </div>
        )}

        {/* Admin Employee View — super-admin only (D16). */}
        {userRole.admin && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.AdminEmployeeView ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.AdminEmployeeView)}
          >
            {translator.translate("AdminEmployeeView")}
          </div>
        )}

        {/* Employee Roles (TAS-668): Manage employee roles edits, Edit employee role reads. */}
        {(can(userRole, 'manageEmployeeRoles') || can(userRole, 'editEmployeeRole')) && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.RolePage ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.RolePage)}
          >
            {translator.translate("RolePage")}
          </div>
        )}

        {can(userRole, 'importTasks') && config.allow2020PieceImport && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.PieceImport ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.PieceImport)}
          >
            {translator.translate("taskImport")}
          </div>
        )}

        {can(userRole, 'importTasks') && config.allowRiceServicesPieceImport && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.RiceServicesPieceImport ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.RiceServicesPieceImport)}
          >
            {translator.translate("riceServicesPieceImport")}
          </div>
        )}

        {can(userRole, 'importTasks') && config.allowUpfPieceImport && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.UPFPieceBudgetImport ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.UPFPieceBudgetImport)}
          >
            {translator.translate("upfPieceBudgetImport")}
          </div>
        )}

        {can(userRole, 'viewAccountCodes') && (
            <div
            className={`sidebar-box ${
              selectedPage === Page.AccountCode ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.AccountCode)}
          >
            {translator.translate("AccountCodePage")}
          </div>
        )}

        {can(userRole, 'managePayPeriods') && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.PayPeriods ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.PayPeriods)}
          >
            {translator.translate("PayPeriods")}
          </div>
        )}

        {can(userRole, 'manageLocations') && (
            <div
            className={`sidebar-box ${
              selectedPage === Page.Locations ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.Locations)}
          >
            {translator.translate("Locations")}
          </div>
        )}

        {can(userRole, 'viewReports') && config.showReportsPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.Reports ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.Reports)}
          >
            {translator.translate("Reports")}
          </div>
        )}

        {/* Misc Tasks → ViewMiscTasks. The pure-supervisor exclusion applies to the
            LEGACY page only, which renders every red and filters client-side. With
            two-level approval on, App.tsx renders the v2 queue instead — that one is
            scoped server-side and routed supervisors are exactly who it is for. Kept
            byte-identical in navConfig.tsx so both shells stay in lockstep. */}
        {can(userRole, 'viewMiscTasks') && config.showMiscTasksPage &&
          (config.miscTaskTwoLevelApproval === true ||
            userRole.admin || userRole.payroll || !userRole.supervisor ||
            (miscTaskApprovalConfigs && miscTaskApprovalConfigs.length > 0)) && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.MiscellaneousTasks ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.MiscellaneousTasks)}
          >
            {translator.translate("MiscellaneousTasks")}
          </div>
        )}

        {can(userRole, 'viewTimesheetComments') && config.showCommentsPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.Comments ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.Comments)}
          >
            Timesheet Comments
          </div>
        )}

        {can(userRole, 'viewModifiedDuty') && config.showModifiedDuty && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.ModifiedDuty ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.ModifiedDuty)}
          >
            {translator.translate("ModifiedDuty")}
          </div>
        )}

        {config.showProjectsPage && userRole.viewProjectsPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.Projects ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.Projects)}
          >
            {translator.translate("Projects")}
          </div>
        )}

        {userRole.viewAllProjectsMap && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.ProjectMap ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.ProjectMap)}
          >
            {translator.translate("ProjectMap")}
          </div>
        )}

        {canViewWorkOrders(userRole) && config.showWorkOrdersPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.WorkOrders ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.WorkOrders)}
          >
            Work Orders
          </div>
        )}

        {canViewWorkOrders(userRole) && config.showChangeOrdersPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.ChangeOrders ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.ChangeOrders)}
          >
            Change Orders
          </div>
        )}

        {/* Render the rest of the accordion categories */}
        {Object.entries(pageCategories).map(([category, pages]) => {
          if (
            !shouldShowCategory(category) 
            || category === 'Home' 
          ) return null;
  
          return (
            <div key={category} style={{ width: '100%'}}>
              <div
                className="sidebar-box category-header"
                onClick={() => toggleCategory(category)}
              >
                <span className="arrow">{openCategories[category] ? '▼' : '▶'}</span>
                {translator.translate(category)}
              </div>
              {openCategories[category] && (
                <div className="category-pages">
                  {pages.map((page) => (
                    <div
                      key={page}
                      className={`sidebar-box page-link ${selectedPage === page ? 'selected' : ''}`}
                      onClick={() => handlePageClick(page)}
                    >
                      {translator.translate(page)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {userRole.admin && config.showSyncStatusPage && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.SyncStatus ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.SyncStatus)}
          >
            {translator.translate("syncStatus")}
          </div>
        )}
        {can(userRole, 'viewSelfieExceptions') && config.showPhotoPages && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.SelfieVerificationExceptions ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.SelfieVerificationExceptions)}
          >
            {translator.translate("selfieVerificationExceptions")}
          </div>
        )}
        {userRole.admin && config.showTimeOffConfig && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.TimeOffConfiguration ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.TimeOffConfiguration)}
          >
            {translator.translate("timeOffConfiguration")}
          </div>
        )}
        {can(userRole, 'processPayroll') && config.showProcessPayroll && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.ProcessPayroll ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.ProcessPayroll)}
          >
            {translator.translate("processPayroll")}
          </div>
        )}
        {userRole.admin && config.showUnionClassificationPages && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.UnionWages ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.UnionWages)}
          >
            {translator.translate("unionWages")}
          </div>
        )}
        {can(userRole, 'viewGpsExceptions') && config.showGpsExceptions && (
          <div
            className={`sidebar-box ${
              selectedPage === Page.GpsExceptions ? 'selected' : ''
            }`}
            onClick={() => handlePageClick(Page.GpsExceptions)}
          >
            GPS Exceptions
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    setSelectedPage(page);
  }, [page]);

  return (
    <div className="layout-container">
      <div className="sidebar">
        <div className="sidebar-inner">
          {/* Use the dynamic logo from the config */}
          <CompanyLogo src={companyLogo} alt="Sidebar Logo" className="sidebar-image" />
          {!loggedIn ? (
            <h4>{translator.translate("Welcome")}!</h4>
          ) : (
            <h4>{translator.translate("Welcome")}, {userName}!</h4>
          )}
          <div className="sidebar-links">
            {loggedIn && renderAccordion()}
          </div>
          {loggedIn && (
            <button className="btn logout-btn" onClick={handleLogout}>{translator.translate("SignOut")}</button>
          )}
        </div>
      </div>

      <div className="main-content">
        <div className="content-wrapper">
          {employeeOnly ? (
            // Employee-only login (TAS-667): no admin pages until the Phase 3 portal.
            <div className="login-container">
              <h2>Employee time tracking is coming soon</h2>
              <p>Your account is set up for employee access. Clocking in and out, timesheets and time off will be available here soon.</p>
              <ActionButton title={translator.translate("SignOut")} onAction={handleLogout} isEnabled={true} />
            </div>
          ) : !loggedIn ? (
            <div className="login-container">
              <h2>{translator.translate("PleaseSignIn")}</h2>
              <p role="alert" style={{ minHeight: '3em', visibility: loginError ? 'visible' : 'hidden' }}>{loginError ?? ''}</p>
              <ActionButton title={translator.translate("SignIn")} onAction={handleLogin} isEnabled={!loggedIn} />
            </div>
          ) : (
            <>{children || <Outlet />}</>
          )}
        </div>
      </div>
    </div>
  );
};

export default Layout;